import { Table } from 'antd';
import naturalCompare from 'natural-compare-lite';
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { DependenciesMap } from 'api/project';
import { DependentTask, Task } from 'api/tasks';
import { Loading } from 'components/atoms/loading';
import { Select } from 'components/molecules/tasks/details/dependent-tasks/styled';
import { TaskName } from 'components/organisms/templates/commons/name-field';
import {
  BodyShort01,
  HelperText,
  SingleLineEllipsis,
  Text01,
} from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import styles from './dependent-tasks.module.css';

interface AddDependentTaskProps {
  taskId: number;
  onChange: (value: DependentTask) => void;
  canFocusImmediately?: boolean;
  existingTasks: number[];
  onFetchTasks(): Promise<DependentTask[]>;
  dependenciesMap?: DependenciesMap;
}

const SecondaryRow = styled.div`
  ${HelperText};
  ${SingleLineEllipsis};
`;

const PrimaryRow = styled.div`
  ${BodyShort01};
  ${SingleLineEllipsis};
  ${Text01};
`;

const TextWrapper = styled.div`
  width: 90%;
`;

const getDependencyBlackList = (
  taskId: number,
  allTasks: DependentTask[],
): number[] => {
  const blackList: number[] = [taskId];

  /**
   * Blacklist all children down the tree
   */
  const getChildrenFor = [taskId];

  while (getChildrenFor.length) {
    const _id = getChildrenFor.shift();
    const subTaskIds = (
      allTasks.find((_t) => _t.taskId === _id)?.subTasks || []
    )
      // in templates, subTasks is maintained as ids instead of objects
      .map((_t) => _t.taskId ?? _t);

    blackList.push(...subTaskIds);
    getChildrenFor.push(...subTaskIds);
  }

  /**
   * Blacklist all parent up the tree
   */
  const getParentFor = [taskId];

  while (getParentFor.length) {
    const _id = getParentFor.shift();
    const _target = allTasks.find((_t) => _t.taskId === _id);
    const parentId =
      _target?.parentTaskObject?.taskId ??
      // in templates, parentTaskObject is set under the key parentTask
      (_target?.parentTask as unknown as Task)?.taskId;

    if (parentId) {
      blackList.push(parentId);
      getParentFor.push(parentId);
    }
  }

  /**
   * Blacklist all dependencies
   */
  const getDependentsFor = [taskId];

  for (let index = 0; index < getDependentsFor.length; index += 1) {
    const _id = getDependentsFor[index];

    const _target = allTasks.find((_t) => _t.taskId === _id);
    const parentId =
      _target?.parentTaskObject?.taskId ??
      // in templates, parentTaskObject is set under the key parentTask
      (_target?.parentTask as unknown as Task)?.taskId;

    let parentDependencyIds: number[] = [];

    const dependencyIds =
      allTasks
        .filter((t) => t.dependentTasks?.find((_t) => _t.taskId === _id))
        .map((_t) => _t.taskId) || [];

    /**
     * Blacklist all dependencies on parent,
     * since dependency on parent is equivalent to dependency on the entire parent tree.
     */
    if (parentId) {
      parentDependencyIds =
        allTasks
          .filter((t) => t.dependentTasks?.find((_t) => _t.taskId === parentId))
          .map((_t) => _t.taskId) || [];
    }

    const validList = [...dependencyIds, ...parentDependencyIds].filter(
      (id) => !blackList.includes(id),
    );

    blackList.push(...validList);
    getDependentsFor.push(...validList);
  }

  return blackList;
};

const getTasksForDependencySelection = (
  taskId: number,
  tasks: DependentTask[],
  existingTasks: number[],
  dependenciesMap?: DependenciesMap,
): DependentTask[] => {
  const filteredTasks = tasks.filter(
    (t) =>
      ![...existingTasks, ...getDependencyBlackList(taskId, tasks)].includes(
        t.taskId,
      ),
  );

  if (dependenciesMap) {
    const map = dependenciesMap.taskMap;
    const result = filteredTasks.filter((task) => {
      return !map[task.taskId] || !map[task.taskId].includes(taskId);
    });

    return result;
  }

  return filteredTasks;
};

export const AddDependentTask: FC<AddDependentTaskProps> = (props) => {
  const { onChange, taskId, existingTasks, onFetchTasks, dependenciesMap } =
    props;

  const [selected, setSelected] = useState<number>();

  const [tasks, setTasks] = useState<DependentTask[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [allTasks, setAllTasks] = useState<DependentTask[]>([]);

  useEffect(() => {
    setSelected(undefined);
  }, [existingTasks]);

  const handleChange = (_selected) => {
    const selectedTask = tasks.find((t) => t.taskId === _selected);

    if (selectedTask) {
      onChange(selectedTask);
    }

    setSelected(_selected);
  };

  const handleOnDropdownVisibleChange = (open: boolean) => {
    if (open) {
      setIsLoading(true);
      onFetchTasks()
        .then((res) => {
          const sortedData = res.sort((a, b) =>
            naturalCompare(a.taskName.toLowerCase(), b.taskName.toLowerCase()),
          );

          setAllTasks(
            getTasksForDependencySelection(
              taskId,
              sortedData,
              existingTasks,
              dependenciesMap,
            ),
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    setTasks(
      getTasksForDependencySelection(
        taskId,
        allTasks,
        existingTasks,
        dependenciesMap,
      ),
    );
  }, [existingTasks, allTasks, taskId, dependenciesMap]);

  const handleOnSearch = (_searchText: string) => {
    if (_searchText) {
      const result = allTasks.filter((t) =>
        t.taskName.toLowerCase().includes(_searchText.toLowerCase()),
      );

      setTasks(result);
    } else {
      setTasks(allTasks);
    }
  };

  const search = useTranslation('placeholder.search');

  const COLUMNS = [
    {
      width: '100%',
      render: () => (
        <Select
          data-cy="dependent_task.search_input"
          showSearch
          autoClearSearchValue
          defaultActiveFirstOption={false}
          showArrow={false}
          bordered={false}
          size="small"
          value={selected}
          placeholder={search}
          filterOption={false}
          loading={isLoading}
          onSearch={handleOnSearch}
          onDropdownVisibleChange={handleOnDropdownVisibleChange}
          notFoundContent={isLoading ? <Loading size="small" /> : undefined}
          onChange={handleChange}
        >
          {tasks.map((t) => (
            <Select.Option key={t.taskId} value={t.taskId}>
              <TextWrapper>
                <PrimaryRow>
                  <TaskName taskName={t.taskName} />
                </PrimaryRow>
                <SecondaryRow>{t.projectPhase?.projectPhaseName}</SecondaryRow>
              </TextWrapper>
            </Select.Option>
          ))}
        </Select>
      ),
    },
  ];

  return (
    <Table
      size="small"
      rowKey="taskId"
      className={styles.table}
      rowClassName={styles.row}
      showHeader={false}
      pagination={false}
      columns={COLUMNS}
      dataSource={[{}]}
    />
  );
};
